import { Button, Dropdown, Menu, Popover, Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, { CSSProperties, useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { Field, GridCell, GridCellChangeRequest } from '../models';
import { LocalImage } from '.';
import { getApiBaseUrl } from '../utils';
import { CaretDownFilled } from '@ant-design/icons';

export function CellValue(props: {
  originalValue?: any;
  cell?: GridCell | GridCellChangeRequest;
  field?: Field;
  highlight?: string[];
  isObsolete?: boolean;
  missingReferenceStyle?: boolean;
  showNullText?: boolean;
  nullTextStyle?: boolean;
  copyable?: boolean;
  isEllipsis?: boolean;
  showLink?: boolean;
  inheritColor?: boolean;
  picklistImageInline?: boolean;
}) {
  const missingReferenceColor = '#f5212d';

  const { Text } = Typography;

  const [isNull, setIsNull] = useState(false);
  const [value, setValue] = useState<any>();
  const [valueStr, setValueStr] = useState<any>();
  const [renderRichText, setRenderRichText] = useState(false);
  const [picklistImage, setPicklistImage] = useState<string>();
  const [picklistImages, setPicklistImages] = useState<any[]>();

  const baseApiUrl = getApiBaseUrl() + '/api/';

  useEffect(() => {
    let cellValue;
    let cellValueStr;
    let cellValueRef;

    if (props.originalValue != null && props.originalValue !== undefined) {
      const isRichText = props.field?.isRichTextEditor || props.field?.dataType === 'DYNAMIC' || props.field?.dataType === 'RULEBASED';
      setRenderRichText(isRichText);

      cellValue = props.originalValue;
      cellValueStr = cellValue;
      cellValueRef = props.cell?.valueRef;

      if (isRichText) {
        cellValue = <div style={{ display: 'flex' }} dangerouslySetInnerHTML={{ __html: cellValue }} />;
        cellValueStr = cellValue;
      } else if (props.field?.dataType === 'STRING' || props.field?.dataType === 'TEXT') {
        cellValue = <span style={{ whiteSpace: props.isEllipsis ? 'unset' : 'pre-line' }}>{cellValue}</span>;
        cellValueStr = cellValue;
      }

      if (props.field?.dataType === 'DATE') {
        cellValue = moment(cellValue).format('DD.MM.yyyy');
        cellValueStr = cellValue;
      }

      if (props.field?.dataType === 'DATETIME') {
        cellValue = moment(cellValue).format('DD.MM.yyyy HH:mm');
        cellValueStr = cellValue;
      }

      if (props.field?.dataType === 'FILE_UPLOAD') {
        if (props.isEllipsis) {
          const filesMenu = (
            <Menu>
              {cellValue?.map((cv: string) => {
                const fileName = cv.split('/').pop();
                const url = `${baseApiUrl}files/${props.field?.id}/${fileName}`;
                return (
                  <Menu.Item key={fileName}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `<a href="${url}" target="_blank">${fileName}</a>`,
                      }}
                    ></div>
                  </Menu.Item>
                );
              })}
            </Menu>
          );
          cellValue = (
            <Dropdown overlay={filesMenu} trigger={['click']}>
              <Button type="link" size="small">
                Dosyalar ({cellValue?.length}) <CaretDownFilled />
              </Button>
            </Dropdown>
          );
        } else {
          cellValue = cellValue?.map((cv: string) => {
            const fileName = cv.split('/').pop();
            const url = `${baseApiUrl}files/${props.field?.id}/${fileName}`;
            return (
              <div
                key={fileName}
                dangerouslySetInnerHTML={{
                  __html: `<a href="${url}" target="_blank">${fileName}</a>`,
                }}
              ></div>
            );
          });
        }
        cellValueStr = cellValueStr.join(', ');
      }

      if (props.field?.dataType === 'PICKLIST') {
        if ((cellValueRef as string)?.startsWith('!!!')) {
          cellValue = <span style={{ fontStyle: 'italic', color: missingReferenceColor }}>{cellValue.replace('!!!', '')}</span>;
        } else {
          const picklistValue = props.field?.picklistValues.find(pv => pv.id === props.cell?.valueRef);
          if (picklistValue?.image) {
            setPicklistImage(picklistValue.image);
            cellValue = (
              <div style={{ display: 'inline-block' }}>
                <LocalImage
                  src={`${baseApiUrl}fields/picklist-image?name=${picklistValue.image}`}
                  alt={picklistValue.val}
                  style={{
                    height: props.picklistImageInline ? '18px' : 'auto',
                    width: props.picklistImageInline ? 'auto' : props.field.picklistImageWidth + 'px',
                    marginRight: '0.5rem',
                  }}
                />
                <span className="option-text">{picklistValue.val}</span>
              </div>
            );
          }
        }
      }

      if (props.field?.dataType === 'MULTIPICKLIST') {
        const rawValues: string[] = cellValue.split(props.field?.arrayValueSeparator);
        const rawValueRefs: string[] = (props.cell?.valueRef as string).split('###');
        const cellValueItems = [];
        const picklistImagesList = [];

        for (let i = 0; i < rawValues.length; i++) {
          const rawValue = rawValues[i].trim();
          const rawValueRef = rawValueRefs[i];
          if (rawValueRef.startsWith('!!!')) {
            const cellValueItem = (
              <>
                <span key={props.field?.id + '_span_' + i} style={{ fontStyle: 'italic', color: missingReferenceColor }}>
                  {rawValue}
                </span>
                {i + 1 < rawValues.length && <span>{props.field?.arrayValueSeparator}</span>}
              </>
            );
            cellValueItems.push(cellValueItem);
          } else {
            const rawValueRef = rawValueRefs[i];
            const picklistValue = props.field?.picklistValues.find(pv => pv.id === rawValueRef);

            let cellValueItem = (
              <span key={props.field?.id + '_span_' + i}>
                {rawValue}
                {i + 1 < rawValues.length && (
                  <span style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}>{props.field?.arrayValueSeparator}</span>
                )}
              </span>
            );

            if (picklistValue?.image) {
              picklistImagesList.push({ index: i, image: picklistValue.image });

              cellValueItem = (
                <div key={props.field?.id + '_span_' + i} style={{ display: 'inline-block' }}>
                  <LocalImage
                    src={`${baseApiUrl}fields/picklist-image?name=${picklistValue.image}`}
                    alt={picklistValue.val}
                    style={{
                      height: props.picklistImageInline ? '18px' : 'auto',
                      width: props.picklistImageInline ? 'auto' : props.field.picklistImageWidth + 'px',
                      marginRight: '0.5rem',
                    }}
                  />
                  <span className="option-text">{rawValue}</span>
                  {i + 1 < rawValues.length && (
                    <span style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}>{props.field?.arrayValueSeparator}</span>
                  )}
                </div>
              );
            }

            cellValueItems.push(cellValueItem);
          }
        }

        if (picklistImagesList?.length) {
          setPicklistImages(picklistImagesList);
        }

        if (cellValueItems?.length) {
          cellValue = <>{cellValueItems.map(i => i)}</>;
        }
      }

      if (props.field?.dataType === 'BOOLEAN') {
        cellValue = props.originalValue === true || props.originalValue === 'true' ? 'Evet' : 'Hayır';
        cellValueStr = cellValue;
      }

      if (props.field?.dataType === 'FOREIGNKEY' && (cellValueRef as string)?.startsWith('!!!')) {
        cellValue = <span style={{ fontStyle: 'italic', color: missingReferenceColor }}>{cellValue.replace('!!!', '')}</span>;
      }

      if (props.field?.dataType === 'MULTIFOREIGNKEY' && (cellValueRef as string)?.includes('!!!')) {
        const rawValues: string[] = cellValue.split(props.field?.arrayValueSeparator);
        const rawValueRefs: string[] = (cellValueRef as string).split('###');
        const cellValueItems = [];

        for (let i = 0; i < rawValues.length; i++) {
          const rawValue = rawValues[i].trim();
          const rawValueRef = rawValueRefs[i];
          if (rawValueRef.startsWith('!!!')) {
            const cellValueItem = (
              <>
                <span key={props.field?.id + '_span_' + i} style={{ fontStyle: 'italic', color: missingReferenceColor }}>
                  {rawValue}
                </span>
                {i + 1 < rawValues.length && <span>{props.field?.arrayValueSeparator}</span>}
              </>
            );
            cellValueItems.push(cellValueItem);
          } else {
            const cellValueItem = (
              <span key={props.field?.id + '_span_' + i}>
                {rawValue}
                {i + 1 < rawValues.length && <span>{props.field?.arrayValueSeparator}</span>}
              </span>
            );
            cellValueItems.push(cellValueItem);
          }
        }
        if (cellValueItems?.length) {
          cellValue = <>{cellValueItems.map(i => i)}</>;
        }
      }
    }

    if((props.cell as GridCell)?.errors && (props.cell as GridCell).errors?.findIndex(x => x === 'Onaylanmamış değer') > -1) {
      cellValue = <span style={{ fontStyle: 'italic', color: '#c2c2c2' }}>{cellValueStr}</span>;
    }

    if (cellValue == null || cellValue === undefined || cellValue === '') {
      setIsNull(true);
      if (props.showNullText) {
        cellValue = 'Boş';
        cellValueStr = cellValue;
      }
    } else {
      setIsNull(false);
    }

    setValue(cellValue);
    setValueStr(cellValueStr);
  }, [
    props.cell,
    props.field?.dataType,
    props.field?.id,
    props.field?.arrayValueSeparator,
    props.field?.picklistValues,
    props.missingReferenceStyle,
    props.originalValue,
    props.showNullText,
    props.field?.picklistImageWidth,
    baseApiUrl,
    props.picklistImageInline,
    props.field?.isRichTextEditor,
    props.isEllipsis,
  ]);

  const textStyle: CSSProperties = {
    fontStyle: props.isObsolete || (isNull && props.showNullText) ? 'italic' : 'inherit',
    color: (props.isObsolete || (isNull && props.showNullText && props.nullTextStyle)) && !props.inheritColor ? '#cccccc' : 'inherit',
    display: 'inline-block',
    width: '100%',
  };

  if (!props.highlight?.length) {
    const copyable = !isNull && props.copyable ? { text: valueStr } : undefined;
    return (
      <div
        style={{
          width: '100%',
          whiteSpace: props.isEllipsis ? 'nowrap' : 'unset',
          maxHeight: props.isEllipsis ? '20px' : undefined,
          overflow: props.isEllipsis ? 'hidden' : 'unset',
          wordBreak: props.isEllipsis ? 'unset' : 'break-word',
          overflowWrap: props.isEllipsis ? 'unset' : 'break-word',
        }}
      >
        {props.field?.dataType === 'PICKLIST' && picklistImage ? (
          <Popover
            content={
              <div>
                <LocalImage
                  src={`${baseApiUrl}fields/picklist-image?name=${picklistImage}`}
                  alt={valueStr}
                  style={{ width: props.field.picklistImageWidth * 2 + 'px', marginRight: '0.5rem' }}
                />
                <span>{valueStr}</span>
              </div>
            }
          >
            {value}
          </Popover>
        ) : props.field?.dataType === 'MULTIPICKLIST' && (picklistImages?.length || 0) > 0 ? (
          <Popover
            content={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {(valueStr as string)?.split(props.field?.arrayValueSeparator).map((value, index, values) => {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {picklistImages?.some(img => img.index === index) && (
                        <LocalImage
                          src={`${baseApiUrl}fields/picklist-image?name=${picklistImages.find(img => img.index === index).image}`}
                          alt={valueStr}
                          style={{ width: (props.field?.picklistImageWidth || 0) * 2 + 'px', marginRight: '0.5rem' }}
                        />
                      )}
                      <span>{value}</span>
                      {index + 1 < values.length && (
                        <span style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}>{props.field?.arrayValueSeparator}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            }
          >
            {''}
            {value}
          </Popover>
        ) : (
          <Tooltip title={renderRichText ? value : valueStr}>
            <Text style={textStyle} copyable={!copyable ? undefined : { text: props.originalValue }}>
              {props.field?.dataType === 'URL' && props.showLink ? (
                <a href={value} target="_blank" rel="noreferrer">
                  {value}
                </a>
              ) : (
                value
              )}
            </Text>
          </Tooltip>
        )}
      </div>
    );
  } else {
    return (
      <div
        style={{
          width: '100%',
          whiteSpace: props.isEllipsis ? 'nowrap' : 'unset',
          maxHeight: props.isEllipsis ? '20px' : undefined,
          overflow: props.isEllipsis ? 'hidden' : 'unset',
          wordBreak: props.isEllipsis ? 'unset' : 'break-word',
          overflowWrap: props.isEllipsis ? 'unset' : 'break-word',
        }}
      >
        <Tooltip title={valueStr}>
          <Highlighter
            searchWords={props.highlight}
            autoEscape={true}
            textToHighlight={value || ''}
            highlightStyle={{ backgroundColor: '#ffff00', padding: '0.2rem 0', width: 'auto' }}
            unhighlightStyle={{ width: 'auto' }}
          />
        </Tooltip>
      </div>
    );
  }
}
